import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { api } from "@/lib/api";

const inp = "w-full rounded-xl border border-brand-line bg-white px-4 py-3 font-body text-sm text-brand-ink outline-none transition-colors duration-300 placeholder:text-brand-ink/35 focus:border-brand-magenta";
const EMPTY = { name: "", email: "", phone: "", organization: "", interest: "", message: "" };

const BookingDialog = ({ open, interest = "", onClose }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  useEffect(() => {
    if (open) setF({ ...EMPTY, interest });
  }, [open, interest]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!f.name.trim() || !f.email.trim() || !f.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/enquiry", f);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send your enquiry. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    onClose();
    setTimeout(() => { setF(EMPTY); setDone(false); }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent data-testid="booking-dialog" className="max-w-lg rounded-[24px] border-brand-line bg-brand-cream p-0">
        <div className="p-7 sm:p-9">
          {done ? (
            <div className="py-6 text-center" data-testid="booking-success-message">
              <span className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full brand-gradient text-white">
                <CheckCircle2 className="h-7 w-7" />
              </span>
              <h3 className="font-heading text-2xl font-semibold text-brand-ink">Thank you, {f.name.split(" ")[0]}!</h3>
              <p className="mx-auto mt-3 max-w-sm font-body text-sm leading-relaxed text-brand-ink/65">
                Your enquiry has been received. Our team will get back to you within one working day.
              </p>
              <button type="button" onClick={close} data-testid="booking-close-button"
                className="mt-7 inline-flex items-center justify-center rounded-full bg-brand-ink px-7 py-3 font-body text-sm font-semibold text-white transition-transform duration-300 hover:scale-[1.04]">
                Done
              </button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <p className="mb-2 inline-flex items-center gap-2 font-heading text-xs font-bold uppercase tracking-[0.2em] text-brand-magenta">
                  <Sparkles className="h-3.5 w-3.5" /> Book an Experience
                </p>
                <DialogTitle className="font-heading text-2xl font-semibold text-brand-ink sm:text-3xl">Tell us about your team</DialogTitle>
                <DialogDescription className="font-body text-sm text-brand-ink/60">
                  Fill this in and we will get back to you within one working day.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="mt-6 space-y-3" data-testid="booking-form">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <input required className={inp} placeholder="Your name *" value={f.name} onChange={set("name")} data-testid="booking-name-input" />
                  <input required type="email" className={inp} placeholder="Work email *" value={f.email} onChange={set("email")} data-testid="booking-email-input" />
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <input className={inp} placeholder="Phone" value={f.phone} onChange={set("phone")} data-testid="booking-phone-input" />
                  <input className={inp} placeholder="Organization" value={f.organization} onChange={set("organization")} data-testid="booking-org-input" />
                </div>
                <input className={inp} placeholder="Program or experience you are interested in" value={f.interest} onChange={set("interest")} data-testid="booking-interest-input" />
                <textarea required rows={3} className={inp} placeholder="Your message *" value={f.message} onChange={set("message")} data-testid="booking-message-input" />
                <button type="submit" disabled={busy} data-testid="booking-submit-button"
                  className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-magenta px-7 py-4 font-body text-sm font-semibold text-white transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60">
                  {busy ? "Sending…" : "Send Enquiry"}
                  {!busy && <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
                </button>
              </form>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BookingDialog;
